"use client";

import { useEffect, useRef } from "react";
import { useParams } from "next/navigation";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import {
	type OfferIntegrityInput,
	type OfferIntegrityOptions,
	findOfferIntegrityIssues,
} from "@/shared/offer-integrity";

/**
 * Reports offers whose fields contradict each other to Datadog. The UI still
 * renders whatever FLOW sent; this only makes the bad data visible so it can be
 * chased on the backend instead of being quietly rounded away on screen.
 */
export const useOfferIntegrityWarning = (
	offer: OfferIntegrityInput | undefined,
	task: string,
	{ appliedRefinance }: OfferIntegrityOptions = {}
) => {
	const { flowId } = useParams<{ flowId?: string }>();
	const reportedRef = useRef<string | null>(null);

	useEffect(() => {
		if (!offer) return;

		const issues = findOfferIntegrityIssues(offer, { appliedRefinance });
		if (issues.length === 0) return;

		// Re-render with the same complaints should not re-log.
		const fingerprint = issues.join("|");
		if (reportedRef.current === fingerprint) return;
		reportedRef.current = fingerprint;

		void sendClientLog({
			level: "warn",
			event: LogEvent.FlowOfferInconsistent,
			message: "Offer data from FLOW is inconsistent",
			task,
			flowId,
			context: { offer, issues },
		});
	}, [offer, task, flowId, appliedRefinance]);
};
